import {Component, computed, inject} from '@angular/core';
import {FormBuilder, ReactiveFormsModule} from '@angular/forms';
import {TranslocoDirective} from '@jsverse/transloco';
import {ActivatedRoute, RouterLink} from '@angular/router';
import {environment} from '@environments/environment';
import {ArrowLeftIcon, LucideAngularModule} from 'lucide-angular';
import {RouteGenerator} from '@core/route-generator';
import {PlaceDTO, PlaceRank, PlaceStats} from '@domain/place/place.dto';
import {UI_ROUTES} from '@core/ui.routes';
import {toSignal} from '@angular/core/rxjs-interop';
import {ActivitiesPreviewComponent} from '@domain/activity/component/activities-preview/activities-preview.component';
import {PlaceUtil} from '@domain/place/place.util';

@Component({
  selector: 'app-public-place-home',
  imports: [
    ReactiveFormsModule,
    TranslocoDirective,
    LucideAngularModule,
    ActivitiesPreviewComponent,
    RouterLink
  ],
  templateUrl: './place-home.component.html',
  styleUrl: './place-home.component.scss'
})
export class PlaceHomeComponent {

  private readonly route = inject(ActivatedRoute);
  private readonly data = toSignal(this.route.data);
  protected readonly place = computed(() => this.data()?.place as PlaceDTO);
  protected readonly stats = computed(
    () => this.data()?.stats as PlaceStats
  );

  protected readonly placeTitle = computed(() => {
    const place = this.place();
    const parent = place.parent;
    const parentTitle = parent?.translation?.name ? `(${parent.translation.name})` : null;
    return [place.code, place.translation?.name, parentTitle]
      .filter(s => !!s)
      .join(' ');
  });

  protected readonly activities = computed(() => this.stats()?.activities ?? []);
  protected readonly hosts = computed(() => this.stats()?.hosts ?? []);
  protected readonly childPlaces = computed(() => this.place()?.children ?? []);
  protected readonly parentPlace = computed(() => this.place()?.parent ?? null);
  protected readonly showParent = computed(() => this.parentPlace()?.rank !== PlaceRank.Region);

  public readonly appName = environment.appName;

  public getPlaceUrl(place: PlaceDTO, parent = this.place()): string {
    const slug = PlaceUtil.getSlug(place, parent)!;
    return RouteGenerator.abs(UI_ROUTES.public.place.home, {slug: slug});
  }

  public getPlaceName(place: PlaceDTO): string {
    return !!place.code
      ? `${place.code} ${place.translation?.name}`
      : place.translation?.name ?? '';
  }

  public readonly icons = {
    back: ArrowLeftIcon
  };

  public readonly routes = {
    dashboard: RouteGenerator.abs(UI_ROUTES.public.dashboard)
  };

}
